// src/shared/content/schema.ts — Zod 4 content schemas for Phase 5 CMS.
//
// Invariants:
//   - authoring shape IS the runtime shape (D-01); no transform layer.
//   - Character (inferred) MUST cover every key of CharacterProfile
//     (compile-time guard: src/shared/content/types.ts _AssertCharacterCoversProfile).
//   - EventTagSchema MUST mirror src/shared/types/events.ts EventTag union verbatim.
//     A divergence breaks CommentarySchema's z.record key contract.
//   - schemaVersion is fixed at literal `1`; bumps require migrate.ts.
//   - DoS guard (T-05-03): explicit upper bounds on every string+array field
//     so an adversary-authored bundle cannot blow out the KV 25MB-per-value
//     ceiling or the parser at the PUT handler.
//
// Source: .planning/phases/05-content-pipeline-characters-admin-cms/05-CONTEXT.md D-01..D-07.

import { z } from 'zod';

// Mirror src/shared/types/events.ts EventTag union exactly. A tag added there
// without updating this enum breaks CommentarySchema at runtime.
export const EventTagSchema = z.enum([
  'catch',
  'free-all',
  'foxguard-called',
  'round-start',
  'round-end',
  'near-miss',
]);

export const CharacterIdSchema = z.enum([
  'eddy',
  'charlie',
  'checka',
  'tommy',
  'teresinha',
  'ze',
  'mary',
  'fiki',
  'gus',
  'william',
  'benny',
]);

// PortraitId enum matches CharacterId (one asset family per character — D-34).
export const PortraitIdSchema = z.enum([
  'eddy',
  'charlie',
  'checka',
  'tommy',
  'teresinha',
  'ze',
  'mary',
  'fiki',
  'gus',
  'william',
  'benny',
]);

export const SizeSchema = z.enum(['small', 'large']);

const UnitInterval = z.number().min(0).max(1);
const PositiveTiles = z.number().positive();

export const CharacterSchema = z.object({
  id: CharacterIdSchema,
  inGameName: z.string().min(1).max(32),
  size: SizeSchema,
  hideSkill: UnitInterval,
  abilities: z.array(z.string()),
  baseSpeedMultiplier: z.number().min(0.5).max(1.5),
  sprintMultiplier: z.number().min(1).max(2),
  sprintDrainRate: z.number().min(0).max(0.2),
  staminaRegenMoving: z.number().min(0).max(0.1),
  staminaRegenIdle: z.number().min(0).max(0.2),
  fear: z.object({
    overall: UnitInterval,
    dark: UnitInterval,
    scorpion: UnitInterval,
    snake: UnitInterval,
    alone: UnitInterval,
  }),
  see: z.object({ base: PositiveTiles, stat: UnitInterval }),
  hear: z.object({ base: PositiveTiles, stat: UnitInterval }),
  // P6 D-06 — per-character pitch multiplier for waffle Howl.rate.
  // Default 1.0 keeps schemaVersion at 1 (additive). T-6-01-01 mitigation:
  // .min(0.7).max(1.4) clamps adversary uploads at the /api/admin/content
  // PUT trust boundary. TODO(P7 POL-06): retune from playtest.
  voicePitch: z.number().min(0.7).max(1.4).default(1.0),
  // P6 D-09 — render-only in P6; AI hearing modulation deferred to P7 via
  // P6_QUIETNESS_HEARING_MULT in src/sim/ai/perception.ts. Object-level
  // .default(...) lets old bundles missing the whole `move` / `hide` block
  // parse cleanly. T-6-01-02 mitigation via UnitInterval (.min(0).max(1)).
  move: z.object({
    quietness: UnitInterval.default(0.5),
  }).default({ quietness: 0.5 }),
  hide: z.object({
    quietness: UnitInterval.default(0.5),
  }).default({ quietness: 0.5 }),
  quirk: z.string().max(2000),
  bio: z.string().max(2000),
  portraitId: PortraitIdSchema,
  unlocked: z.boolean(),
});

export const QuoteSchema = z.object({
  characterId: CharacterIdSchema,
  eventTag: EventTagSchema,
  // DoS guard (T-05-03): cap at 200 lines × 2000 chars so authored bundles
  // stay under KV 25MB. min(1) on each line prevents empty-string spam.
  lines: z.array(z.string().min(1).max(2000)).max(200),
});

// z.record(KeyEnum, ValueType) — Zod 4 REQUIRES explicit key schema
// (vs Zod 3 which inferred `string`).
export const CommentarySchema = z.record(
  EventTagSchema,
  z.array(z.string().min(1).max(2000)).max(500),
);

// P7 POL-06 / D-12 — admin-editable sim tuning. Additive optional-with-default
// section: schemaVersion stays `1` (mirrors the voicePitch precedent at line 87).
// getTuning() in src/sim/content/cache.ts reads these with the sim code constant
// as the EXACT fallback default, so an absent section or missing field is
// behaviourally identical to the baked constant (golden-hash stable).
//
// Every field carries explicit DoS min/max bounds (T-05-03 / T-07-04 convention)
// so an adversary-authored bundle cannot drive a value out of range and brick a
// client. Tick/integer fields use z.number().int(); each .default(...) equals the
// current code constant so a bundle that omits the field round-trips unchanged.
const TuningSchema = z
  .object({
    // Foxguard (src/sim/foxguard/constants.ts)
    foxguardRecentDropoffTicks: z.number().int().min(0).max(1800).default(150),
    foxguardNearBaseTiles: z.number().min(0).max(40).default(6),
    foxguardVelocityPxPerS: z.number().min(0).max(512).default(32),
    foxguardGraceTicks: z.number().int().min(0).max(1800).default(180),
    foxguardShoutCooldownTicks: z.number().int().min(0).max(3600).default(450),
    // Round timing (src/sim/round/constants.ts)
    roundDurationTicks: z.number().int().min(300).max(36000).default(9000),
    headstartDurationTicks: z.number().int().min(0).max(3600).default(240),
    teamswitchDurationTicks: z.number().int().min(0).max(1800).default(210),
    // Awareness (src/sim/ai/awareness/constants.ts)
    awarenessSuspicious: z.number().min(0).max(1).default(0.25),
    awarenessAlerted: z.number().min(0).max(1).default(0.55),
    awarenessSpotted: z.number().min(0).max(1).default(0.85),
    awarenessBaseRate: z.number().min(0).max(1).default(0.22),
    awarenessDecayRate: z.number().min(0).max(1).default(0.05),
    awarenessMaxRangeTiles: z.number().min(0).max(40).default(14),
    // Hearing & noise / coordination (src/sim/ai/coordination/constants.ts)
    blackboardSigmaGrowthPerTick: z.number().min(0).max(10).default(0.5),
    blackboardMaxAgeTicks: z.number().int().min(0).max(3600).default(360),
    shoutWallPenaltyTiles: z.number().min(0).max(20).default(2),
    // Utility weights (src/sim/ai/coordination/constants.ts)
    investigateBase: z.number().min(0).max(2).default(0.4),
    investigateDistanceWeight: z.number().min(0).max(2).default(0.5),
    investigateDuplicateWeight: z.number().min(0).max(2).default(0.3),
    investigateConfidenceWeight: z.number().min(0).max(2).default(0.3),
    // Hearing multiplier (src/sim/ai/perception.ts — pinned at 1 in P6)
    quietnessHearingMult: z.number().min(0).max(4).default(1),
  })
  .optional();

export const ContentBundleSchema = z.object({
  schemaVersion: z.literal(1),
  // Closed roster: exactly 11 (10 family + Benny stub) — D-06.
  characters: z.array(CharacterSchema).length(11),
  quotes: z.array(QuoteSchema).max(1000),
  commentary: CommentarySchema,
  // P7 POL-06 / D-12 — optional admin-editable sim tuning (see TuningSchema).
  tuning: TuningSchema,
});
